/**
 * 맥스매치 목록 — 레거시 `maxMatchInfo.js` 포팅.
 * RDS: PageHeader → FilterCard → DataCard.
 */
import {
  useDownloadMaxMatchExcel,
  useMaxMatchList,
  usePublishMaxMatch,
  useRunMaxMatch,
} from '@/entities/max-match/hooks/useMaxMatch';
import { maxMatchService } from '@/entities/max-match/api/max-match.api';
import { DataCard } from '@/shared/components/DataCard';
import { EmptyState } from '@/shared/components/EmptyState';
import { FilterCard } from '@/shared/components/FilterCard';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import { SimplePagination } from '@/shared/components/SimplePagination';
import { useListSearchParams } from '@/shared/hooks/useListSearchParams';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { FilterRow } from '@/shared/components/ui/row';
import { maxMatchDetailPath } from '@/shared/constants/routes';
import { getCurrentRefYm } from '@/pages/fee-rates/feeRateUtils';
import {
  downloadXlsx,
  MAX_MATCH_EXCEL_HEADER_MAP,
} from '@/shared/utils/xlsxExport';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useNavigate } from '@tanstack/react-router';
import { Download, Play, RefreshCw, Search, Send, Sparkles } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

const getRecentMonths = (): string[] => {
  const now = new Date();
  const months: string[] = [];
  for (let i = 1; i <= 3; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    months.push(`${y}${m}`);
  }
  return months;
};

/** 생성 요청 후 재요청을 막는 시간 — 서버 처리가 길어 중복 실행을 방지한다 */
const RUN_COOLDOWN_MS = 60_000;

const MAX_MATCH_SEARCH_DEFAULTS = {
  page: 1,
  pageSize: 25,
  query: '',
};

export const MaxMatchInfoPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();

  const { values, setValues } = useListSearchParams(MAX_MATCH_SEARCH_DEFAULTS);

  const [keyword, setKeyword] = useState(values.query);

  React.useEffect(() => {
    setKeyword(values.query);
  }, [values.query]);

  const search = React.useMemo(
    () => ({
      query: values.query || undefined,
    }),
    [values.query],
  );

  const pageable = React.useMemo(
    () => ({ page: Math.max(0, values.page - 1), size: values.pageSize }),
    [values.page, values.pageSize],
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(
    getRecentMonths()[0] ?? getCurrentRefYm(),
  );
  const [monthStatus, setMonthStatus] = useState<Record<string, boolean>>({});
  const [runCooldownUntil, setRunCooldownUntil] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const { data, isLoading, isError, error, refetch, isFetching } =
    useMaxMatchList(search, pageable);
  const runMaxMatch = useRunMaxMatch();
  const publishMaxMatch = usePublishMaxMatch();
  const downloadExcel = useDownloadMaxMatchExcel();

  const recentMonths = useMemo(() => getRecentMonths(), []);

  useEffect(() => {
    if (isError && error) handleApiError(error);
  }, [isError, error]);

  useEffect(() => {
    recentMonths.forEach((month) => {
      maxMatchService.getExists(month).then((exists) => {
        setMonthStatus((prev) => ({ ...prev, [month]: exists }));
      });
    });
  }, [recentMonths]);

  useEffect(() => {
    if (runCooldownUntil === null) return;
    const timer = window.setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= runCooldownUntil) setRunCooldownUntil(null);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [runCooldownUntil]);

  const runCooldownSeconds =
    runCooldownUntil === null
      ? 0
      : Math.max(0, Math.ceil((runCooldownUntil - now) / 1000));
  const isRunLocked = runMaxMatch.isPending || runCooldownSeconds > 0;

  const rows = data?.content ?? [];
  const totalCount = data?.totalCount ?? 0;
  // 레거시: 직전 3개월 전부 노출(생성 됨/전 라벨). 미생성만 필터하지 않음(재생성 허용).
  const unpublishedMonths = recentMonths.filter((m) => !monthStatus[m]);
  const publishMonths = recentMonths.filter((m) => monthStatus[m]);

  const openCreateModal = () => {
    // 레거시: 월 목록 첫 항목(보통 미생성 우선이 아니라 recentMonths[0]) 기본 선택
    setSelectedMonth(
      unpublishedMonths[0] ?? recentMonths[0] ?? getCurrentRefYm(),
    );
    setCreateOpen(true);
  };

  const openPublishModal = () => {
    // 레거시: 게시 가능 월(생성됨) 중 첫 항목으로 게시
    setSelectedMonth(publishMonths[0] ?? recentMonths[0] ?? getCurrentRefYm());
    setPublishOpen(true);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setValues({ query: keyword.trim(), page: 1 });
  };

  const handleRun = async () => {
    if (isRunLocked) return;
    const startedAt = Date.now();
    setNow(startedAt);
    setRunCooldownUntil(startedAt + RUN_COOLDOWN_MS);
    try {
      await runMaxMatch.mutateAsync(selectedMonth);
      toast.success('맥스매치 생성을 요청했습니다.');
      setCreateOpen(false);
      setMonthStatus((prev) => ({ ...prev, [selectedMonth]: true }));
    } catch (e) {
      handleApiError(e);
    }
  };

  const handlePublish = async () => {
    // 레거시: publishAvailableMonthList[0] 기준으로 게시
    const monthToPublish = publishMonths[0] ?? selectedMonth;
    if (!monthToPublish || publishMonths.length === 0) {
      toast.error('게시 가능한 월이 없습니다.');
      return;
    }
    try {
      await publishMaxMatch.mutateAsync(monthToPublish);
      toast.success('맥스매치를 게시했습니다.');
      setPublishOpen(false);
    } catch (e) {
      handleApiError(e);
    }
  };

  const handleDownloadAll = async () => {
    try {
      const result = await downloadExcel.mutateAsync({ creationType: 'SYSTEM' });
      downloadXlsx(result, '맥스매치_전체_내역', MAX_MATCH_EXCEL_HEADER_MAP);
    } catch (e) {
      handleApiError(e);
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Sparkles}
        title="맥스매치"
        description="맥스매치 생성·게시 및 사용자별 실행 이력 조회"
        actions={
          <>
            <Button variant="outline" size="sm" onClick={handleDownloadAll}>
              <Download className="mr-2 h-4 w-4" />
              전체 다운로드 (시스템)
            </Button>
            <MenuWriteGate>
              <Button
                size="sm"
                className="ruda-button"
                onClick={openCreateModal}
                disabled={isRunLocked}
              >
                <Play className="mr-2 h-4 w-4" />
                Max Match 생성
                {runCooldownSeconds > 0 && ` (${runCooldownSeconds}초)`}
              </Button>
            </MenuWriteGate>
            <MenuWriteGate>
              <Button variant="outline" size="sm" onClick={openPublishModal}>
                <Send className="mr-2 h-4 w-4" />
                게시
              </Button>
            </MenuWriteGate>
          </>
        }
      />

      <FilterCard>
        <FilterRow gap={12} onSubmit={handleSearch}>
          <div className="space-y-1">
            <Label htmlFor="max-match-query">검색어</Label>
            <Input
              id="max-match-query"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="업체명 또는 사용자 이메일"
              className="w-56"
            />
          </div>
          <div className="flex items-end gap-2">
            <Button type="submit" className="ruda-button">
              <Search className="mr-2 h-4 w-4" />
              검색
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => refetch()}
              disabled={isFetching}
              aria-label="새로고침"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </FilterRow>
      </FilterCard>

      <DataCard>
        {isLoading ? (
          <LoadingSpinner tip="맥스매치 목록을 불러오는 중..." />
        ) : rows.length === 0 ? (
          <EmptyState description="조회된 맥스매치 사용자가 없습니다." />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>업체명</TableHead>
                  <TableHead>사용자 이메일</TableHead>
                  <TableHead>거래 병의원 수</TableHead>
                  <TableHead>max match 사용</TableHead>
                  <TableHead>저장된 max match</TableHead>
                  <TableHead>정산 개월 수</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.userId}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() =>
                      navigate({
                        to: maxMatchDetailPath(row.userId),
                        search: row.companyName
                          ? { companyName: row.companyName }
                          : {},
                      } as never)
                    }
                  >
                    <TableCell>{row.companyName}</TableCell>
                    <TableCell>{row.email}</TableCell>
                    <TableCell>{row.clinicCount}</TableCell>
                    <TableCell>{row.maxMatchRunCount}</TableCell>
                    <TableCell>{row.maxMatchResultCount}</TableCell>
                    <TableCell>{row.settlementMonthCount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <SimplePagination
              page={values.page}
              pageSize={values.pageSize}
              total={totalCount}
              onPageChange={(page) => setValues({ page })}
              onPageSizeChange={(pageSize) => setValues({ pageSize, page: 1 })}
            />
          </>
        )}
      </DataCard>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Max Match 생성 년 월 선택</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label>처방년월</Label>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {recentMonths.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m.slice(0, 4)}.{m.slice(4)}{' '}
                    {monthStatus[m] ? '생성 됨' : '생성 전'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              취소
            </Button>
            <Button
              className="ruda-button"
              onClick={handleRun}
              disabled={isRunLocked}
            >
              생성
              {runCooldownSeconds > 0 && ` (${runCooldownSeconds}초)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Max Match 게시</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label>처방년월</Label>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(publishMonths.length ? publishMonths : recentMonths).map(
                  (m) => (
                    <SelectItem key={m} value={m}>
                      {m.slice(0, 4)}.{m.slice(4)}
                    </SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPublishOpen(false)}>
              취소
            </Button>
            <Button
              className="ruda-button"
              onClick={handlePublish}
              disabled={
                publishMaxMatch.isPending || publishMonths.length === 0
              }
            >
              게시
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
